import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft, GraduationCap, IdentificationCard } from "@phosphor-icons/react";
import { API, useApi, fmtDate } from "../lib/api";
import { Loader } from "../components/kit";
import { SchoolMark, PersonPhoto } from "../components/Photo";

const PRINT_CSS = `
@page { size: 85.6mm 54mm; margin: 0; }
@media print {
  .idc-wrap { padding: 0 !important; gap: 0 !important; display: block !important; zoom: 1 !important; }
  .idc-pair { display: block !important; }
  .idc { box-shadow: none !important; border-radius: 0 !important; break-after: page; page-break-after: always; margin: 0 !important; }
  .idc-pair:last-child .idc:last-child { break-after: auto; page-break-after: auto; }
}
.idc, .idc * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
`;

const Row = ({ k, v, testid }) => (
  <div className="flex gap-[1.2mm] leading-[1.5]"><span className="w-[11mm] shrink-0 text-[5.4pt] uppercase tracking-wide text-slate-500">{k}</span>
    <span className="truncate text-[7pt] font-semibold text-slate-900" data-testid={testid}>{v || "—"}</span></div>
);

function Card({ s, school, i }) {
  return (
    <div className="idc relative flex h-[54mm] w-[85.6mm] shrink-0 flex-col overflow-hidden rounded-[3mm] bg-white text-slate-900 shadow-2xl" data-testid={`id-card-${s.admission_no}`}>
      <div className="flex h-[12.5mm] items-center gap-[2mm] bg-gradient-to-r from-[#0b1a3d] via-[#13306e] to-[#1d4ed8] px-[3mm] text-white">
        <span className="grid h-[9mm] w-[9mm] shrink-0 place-items-center overflow-hidden rounded-full bg-white">
          <SchoolMark school={school} className="h-[7.5mm] w-[7.5mm]" testid={`id-card-logo-${i}`} fallback={<GraduationCap size={18} weight="fill" className="text-blue-700" />} />
        </span>
        <div className="min-w-0">
          <div className="truncate text-[8.6pt] font-bold leading-tight">{school.name}</div>
          <div className="truncate text-[5pt] text-blue-100">{[school.address, school.city].filter(Boolean).join(", ")}{school.phone ? ` · ${school.phone}` : ""}</div>
        </div>
      </div>
      <div className="flex flex-1 items-center gap-[2.5mm] px-[3mm]">
        <div className="h-[29mm] w-[23mm] shrink-0 overflow-hidden rounded-[1.6mm] border-[0.5mm] border-orange-400">
          <PersonPhoto kind="student" person={s} className="!h-full !w-full !rounded-none !ring-0 text-[14pt]" testid={`id-card-photo-${i}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[9.6pt] font-bold leading-tight text-[#0b1a3d]" data-testid={`id-card-name-${i}`}>{s.name}</div>
          <div className="mb-[1mm] text-[6.2pt] font-semibold text-orange-600">{s.class_name}{s.roll_no ? ` · Roll ${s.roll_no}` : ""}</div>
          <Row k="Adm no" v={s.admission_no} testid={`id-card-adm-${i}`} />
          <Row k="DOB" v={s.dob ? fmtDate(s.dob) : ""} />
          <Row k="Blood" v={s.blood_group} />
          <Row k="Parent" v={s.parent_name} />
          <Row k="Phone" v={s.parent_phone} />
        </div>
        <div className="flex shrink-0 flex-col items-center self-start pt-[2.5mm]">
          <QRCodeSVG value={s.admission_no || s.id} size={56} level="M" style={{ width: "15mm", height: "15mm" }} data-testid={`id-card-qr-${i}`} />
          <span className="mt-[0.6mm] text-[4.6pt] text-slate-500">Scan · Adm no</span>
        </div>
      </div>
      <div className="flex h-[4.2mm] items-center justify-between bg-gradient-to-r from-orange-500 to-amber-400 px-[3mm] text-[5pt] font-bold uppercase tracking-[0.18em] text-white">
        <span>Student ID</span><span>{school.academic_year}</span>
      </div>
    </div>
  );
}

function Back({ s, school, i }) {
  return (
    <div className="idc relative flex h-[54mm] w-[85.6mm] shrink-0 flex-col overflow-hidden rounded-[3mm] bg-white text-slate-900 shadow-2xl" data-testid={`id-card-back-${s.admission_no}`}>
      <div className="flex h-[6.5mm] shrink-0 items-center justify-between bg-gradient-to-r from-[#0b1a3d] to-[#1d4ed8] px-[3mm] text-[5.6pt] font-bold uppercase tracking-[0.16em] text-white">
        <span>School rules</span><span className="font-mono tracking-normal">{s.admission_no}</span>
      </div>
      <div className="flex min-h-0 flex-1 gap-[2.5mm] overflow-hidden px-[3mm] pt-[2mm]">
        <ol className="min-w-0 flex-1 list-decimal space-y-[0.4mm] pl-[3mm] text-[5.4pt] leading-[1.25] text-slate-700" data-testid={`id-card-rules-${i}`}>
          {school.id_card_rules.slice(0, 6).map((r, k) => <li key={k}>{r}</li>)}
        </ol>
        <div className="w-[25mm] shrink-0 self-start rounded-[1.6mm] border-[0.35mm] border-rose-300 bg-rose-50 px-[2mm] py-[1.5mm]" data-testid={`id-card-emergency-${i}`}>
          <div className="text-[5pt] font-bold uppercase tracking-wider text-rose-600">In emergency</div>
          <div className="mt-[0.8mm] text-[4.8pt] uppercase text-slate-500">Parent</div>
          <div className="truncate text-[6.4pt] font-semibold">{s.parent_phone || "—"}</div>
          <div className="mt-[0.5mm] text-[4.8pt] uppercase text-slate-500">School</div>
          <div className="truncate text-[6.4pt] font-semibold">{school.phone || "—"}</div>
        </div>
      </div>
      <div className="flex shrink-0 items-end justify-between gap-[3mm] px-[3mm] pb-[1.5mm]">
        <div className="min-w-0 text-[4.8pt] leading-[1.35] text-slate-500">If found, please return to<br /><span className="font-semibold text-slate-700">{school.name}</span>, {[school.address, school.city].filter(Boolean).join(", ")}</div>
        <div className="flex w-[24mm] shrink-0 flex-col items-center">
          <div className="flex h-[7.5mm] items-end">
            {school.has_signature && <img src={`${API}/school/signature?v=${encodeURIComponent(school.signature_version || "")}`} alt="Principal's signature" className="max-h-[7.5mm] max-w-[22mm] object-contain" data-testid={`id-card-signature-${i}`} />}
          </div>
          <div className="w-full border-t-[0.25mm] border-slate-400 pt-[0.4mm] text-center text-[4.8pt] text-slate-600">Principal{school.principal_name ? ` · ${school.principal_name}` : ""}</div>
        </div>
      </div>
      <div className="h-[2mm] shrink-0 bg-gradient-to-r from-orange-500 to-amber-400" />
    </div>
  );
}

export default function IdCards() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const classId = params.get("class_id") || "";
  const studentId = params.get("student_id") || "";
  const qs = new URLSearchParams({ ...(classId && { class_id: classId }), ...(studentId && { student_id: studentId }) }).toString();
  const { data, loading, error } = useApi(`/id-cards?${qs}`);
  const n = data?.students.length || 0;
  const only = !classId && !studentId && data?.classes.length === 1 ? data.classes[0].id : null;
  useEffect(() => {
    if (only) setParams({ class_id: only }, { replace: true });
  }, [only, setParams]);
  return (
    <div className="min-h-screen bg-[#05050a] py-8 px-4 print:bg-white print:p-0">
      <style>{PRINT_CSS}</style>
      <div className="no-print mx-auto mb-8 flex max-w-5xl flex-wrap items-center justify-between gap-3">
        <button className="btn-ghost" onClick={() => nav(-1)} data-testid="idcards-back-btn"><ArrowLeft size={16} /> Back</button>
        <div className="flex flex-wrap items-center gap-3">
          {!studentId && (
            <select className="field !w-56" value={classId} onChange={(e) => setParams(e.target.value ? { class_id: e.target.value } : {})} data-testid="idcards-class-select">
              <option value="">Choose a class</option>
              {data?.classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <span className="text-sm text-zinc-400" data-testid="idcards-count">{n} card{n === 1 ? "" : "s"} · front + back</span>
          <button className="btn-primary" disabled={!n} onClick={() => window.print()} data-testid="idcards-print-btn"><Printer size={16} /> Print / Save PDF</button>
        </div>
      </div>
      {loading ? <Loader /> : error ? <p className="text-center text-rose-300" data-testid="idcards-error">{error}</p> : !n ? (
        <div className="no-print flex flex-col items-center py-20 text-center text-zinc-500" data-testid="idcards-empty">
          <IdentificationCard size={40} weight="duotone" className="mb-3 text-zinc-600" />
          {data?.classes.length ? "Choose a class to see its ID cards." : "You can print ID cards only for the class you teach."}
        </div>
      ) : (
        <div className="idc-wrap mx-auto flex max-w-5xl flex-wrap justify-center gap-8 sm:[zoom:1.45]" data-testid="idcards-sheet">
          {data.students.map((s, i) => (
            <div key={s.id} className="idc-pair flex flex-wrap justify-center gap-4">
              <Card s={s} school={data.school} i={i} />
              <Back s={s} school={data.school} i={i} />
            </div>
          ))}
        </div>
      )}
      <p className="no-print mt-10 text-center text-xs text-zinc-500">Prints front then back for each card at standard ID size (85.6 × 54 mm) — set your card printer to two-sided. In the print dialog set margins to "None".</p>
    </div>
  );
}
